/**
 * Helper functions for units of measurement
 */

const TYPE_DISPLAY_NAMES = {
	'storage': 'Storage sizes',
	'frequency': 'Frequencies',
	'count': 'Counts',
	'duration': 'Durations',
	'duration-minutes': 'Durations (RPO, in minutes)'
};

// Tolerance of the float arithmetic when a value typed in a unit is turned into the base unit.
const WHOLE_NUMBER_TOLERANCE = 1e-6;

/**
 * The option label of a unit: its name, then its symbol when the symbol adds something.
 * "TB (decimal)" and "Terabyte" read apart this way, while "count" is not repeated.
 */
export const unitLabel = (unit) => {
	if (!unit) {
		return '';
	}

	const name = unit.name || unit.symbol || '';
	const symbol = unit.symbol || '';

	if (!symbol || symbol.toLowerCase() === name.toLowerCase()) {
		return name;
	}

	return `${name} (${symbol})`;
};

const compareUnits = (a, b) => {
	const factorDifference = (a.multiplicationFactor ?? 0) - (b.multiplicationFactor ?? 0);

	if (factorDifference !== 0) {
		return factorDifference;
	}

	return unitLabel(a).localeCompare(unitLabel(b));
};

/**
 * Organizes units of measurement into optgroups by type, each group ordered from the smallest unit up
 * (seconds before days, KB before TB).
 * @param {Array} units - Array of unit objects with id, name, type, symbol, multiplicationFactor
 * @returns {Array} Array of optgroup objects with label and options
 */
export const organizeUnitsByType = (units) => {
	if (!units || !Array.isArray(units) || units.length === 0) {
		return [];
	}

	const groupedUnits = units.reduce((acc, unit) => {
		const type = unit.type || 'Other';
		if (!acc[type]) {
			acc[type] = [];
		}
		acc[type].push(unit);
		return acc;
	}, {});

	const optgroups = Object.entries(groupedUnits).map(([type, unitsOfType]) => {
		return {
			label: getTypeDisplayName(type),
			options: [...unitsOfType].sort(compareUnits).map((unit) => {
				return { value: unit.id, label: unitLabel(unit) };
			})
		};
	});

	// Add "None" option at the beginning
	optgroups.unshift({
		label: 'None',
		options: [{ value: null, label: 'None' }]
	});

	return optgroups;
};

/**
 * Gets display name for unit type
 * @param {string} type - Unit type (storage, frequency, count, duration, ...)
 * @returns {string} Display name for the type
 */
const getTypeDisplayName = (type) => {
	return TYPE_DISPLAY_NAMES[type.toLowerCase()] || type;
};

/**
 * Finds a unit by its ID
 * @param {Array} units - Array of unit objects
 * @param {string} unitId - Unit ID to find
 * @returns {Object|null} Unit object or null if not found
 */
export const findUnitById = (units, unitId) => {
	if (!units || !Array.isArray(units) || !unitId) {
		return null;
	}

	return units.find((unit) => { return unit.id === unitId; }) || null;
};

/**
 * Rounds a number that float arithmetic left a hair away from a whole number (4.35 TB in bytes), and
 * leaves a genuinely fractional one alone so the validation can refuse it.
 */
export const snapToWholeNumber = (value) => {
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		return value;
	}

	const rounded = Math.round(value);

	if (Math.abs(value - rounded) <= WHOLE_NUMBER_TOLERANCE * Math.max(1, Math.abs(value))) {
		return rounded;
	}

	return value;
};

/**
 * Converts a value from display unit to base unit
 * @param {number} value - Value in display unit
 * @param {Object} unit - Unit object with multiplicationFactor
 * @returns {number} Value in base unit
 */
export const convertToBaseUnit = (value, unit) => {
	if (!unit || !unit.multiplicationFactor) {
		return value;
	}

	return snapToWholeNumber(value * unit.multiplicationFactor);
};

/**
 * Converts a value from base unit to display unit
 * @param {number} value - Value in base unit
 * @param {Object} unit - Unit object with multiplicationFactor
 * @returns {number} Value in display unit
 */
export const convertFromBaseUnit = (value, unit) => {
	if (!unit || !unit.multiplicationFactor) {
		return value;
	}

	return value / unit.multiplicationFactor;
};
